import { use, useCallback, useMemo, useRef } from "react";
import { GestureResponderEvent, LayoutChangeEvent, View, useWindowDimensions } from "react-native";
import { DropdownConfigType, DropdownManagerContext } from "../contexts/DropdownManager";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useIsScreenSizeMin } from "./useIsScreenSizeMin";

export type UseDropdownProps = {
    component: NonNullable<DropdownConfigType["component"]>;
    withScreenActionsPadding?: boolean;
    position?: "left" | "right";
    yMargin?: number;
} & Pick<DropdownConfigType, "keepOpen" | "onBlur" | "uniqueSelection">;

// Minimum distance between the dropdown and the edges of the screen
const SCREEN_PADDING = 20;

// Our navigation TabBar's height
const SCREEN_ACTIONS_HEIGHT = 110;

export const useDropdown = ({
    component,
    keepOpen = false,
    position = "left",
    withScreenActionsPadding = true,
    onBlur,
    yMargin = 0,
}: UseDropdownProps) => {
    const { setDropdownConfig } = use(DropdownManagerContext);
    const { height: windowHeight, width: windowWidth } = useWindowDimensions();
    const { top: topSafeArea } = useSafeAreaInsets();
    const ref = useRef<View>(null);
    const isLarge = useIsScreenSizeMin("large");

    // SDK 55 / Android edge-to-edge: useWindowDimensions().height already represents the full
    // screen and measureInWindow's y is relative to that window — no Android-specific safe-area
    // compensation needed anymore (previously this added topSafeArea + bottomSafeArea, which
    // double-counted under edge-to-edge).
    const windowHeightWithoutScreenActions = useMemo(
        () =>
            windowHeight -
            (withScreenActionsPadding && !isLarge ? SCREEN_ACTIONS_HEIGHT : 0) -
            SCREEN_PADDING * 2,
        [isLarge, windowHeight, withScreenActionsPadding],
    );

    const onLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const { height: dropdownHeight } = e.nativeEvent.layout;
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            setDropdownConfig((config) => {
                // Handle dropdown too low
                if ((config.position?.y || 0) + dropdownHeight > windowHeightWithoutScreenActions) {
                    return {
                        ...config,
                        position: {
                            ...config.position,
                            y: windowHeightWithoutScreenActions - dropdownHeight,
                            visible: true,
                        },
                    };
                }
                return { ...config, position: { ...config.position, visible: true } };
            });
        },
        [setDropdownConfig, windowHeightWithoutScreenActions],
    );

    const onPress = useCallback(
        (e?: GestureResponderEvent) => {
            const pressX = e?.nativeEvent?.pageX;
            const pressY = e?.nativeEvent?.pageY;

            ref.current?.measureInWindow((x, y, width) => {
                let top = y + yMargin;

                // Handle dropdown too high
                if (top < SCREEN_PADDING + topSafeArea + yMargin) {
                    top = SCREEN_PADDING + topSafeArea + yMargin;
                }

                if (x + width + SCREEN_PADDING > windowWidth) {
                    x = windowWidth - width - SCREEN_PADDING;
                }

                setDropdownConfig({
                    component,
                    position: {
                        x,
                        y: top,
                        width,
                        maxHeight: windowHeightWithoutScreenActions,
                        dropdownPosition: position,
                        pressX,
                        pressY,
                    },
                    keepOpen,
                    onBlur,
                    onLayout,
                });
            });
        },
        [
            yMargin,
            topSafeArea,
            windowWidth,
            setDropdownConfig,
            component,
            windowHeightWithoutScreenActions,
            position,
            keepOpen,
            onBlur,
            onLayout,
        ],
    );

    return { ref, onPress };
};
